import React, { useRef } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import TableBodyCheckbox from '../../../../components/Table/BodyCheck';
import Dropdown from '../../../../components/Dropdown/Default';
import Tooltip from '../../../../components/Tooltip';
import BodyFields from './bodyFields';

import { makeTableRow, generateActiveClass, getUserAccess } from '../../helpers';
import { useTbodyPosition } from '../../../../utils/hooks/useTbodyPosition';

const TableBody = ({ isLoading, isLoadingItems, users, handleSelect, onChangeCheck, values, openModal, config, fields }) => {
  const history = useHistory();
  const location = useLocation();

  const bodyRef = useRef(null);
  const position = useTbodyPosition({ bodyRef, count: users?.length, bodyHeight: '300px' });

  return (
    <tbody
      className={classNames('', { 'tbody-loading': isLoading })}
      style={{ backgroundPositionY: position.y, backgroundPositionX: position.x }}
      ref={bodyRef}
    >
      {users?.map((item, index) => (
        <tr key={index} className={generateActiveClass(values, item)}>
          {fields
            && config
            && makeTableRow(item, fields, config).map((elem, idx) => {
              switch (elem.type) {
              case 'actions': {
                if (getUserAccess(item, config?.access).length > 0) {
                  return (
                    <td className="td-caret" key={idx}>
                      <Dropdown
                        handleSelect={(...props) => handleSelect(...props, 'single')}
                        size="xsmall"
                        title="Actions"
                        items={getUserAccess(item, config?.access)}
                        forMassActions
                        classForBtnDrop="font__weight-med"
                        dropStyle="default"
                        toolbarClass="dropdown-right"
                        openModal={openModal}
                        user={item}
                        id={item.id}
                        item={item}
                        isLoading={isLoadingItems?.includes(item.id)}
                      />
                    </td>
                  );
                }
                return null;
              }

              case 'rates': {
                return (
                  <td key={idx}>
                    <Button
                      bsSize="xsmall"
                      className={`${item.custom_rates === 0 ? 'users-table__rates-btn' : ''} ${
                          item.is_inactive ? 'users-table__btn-suspended' : ''
                        }`}
                      onClick={() => history.push({ pathname: `/users/view/${item.id}`, state: { query: location.search } })}
                    >
                      {item.custom_rates > 0 ? (
                        <>
                          <span>Custom rates</span>
                          {' '}
                          <Badge className="badge-green">{item.custom_rates}</Badge>
                        </>
                      ) : (
                        'Set custom rates'
                      )}
                    </Button>
                  </td>
                );
              }

              case 'discount': {
                const userLevel = config?.discounts ? config.discounts.find((level) => Number(level.id) === Number(item.user_level)) : {};
                const isActiveLevelSystem = !!config?.access?.discount_counts;
                const isCustomDiscount = !!item.discount;

                return (
                  <td key={idx}>
                    {isActiveLevelSystem ? (
                      <Button
                        bsSize="xsmall"
                        className={`${item.is_inactive ? 'users-table__btn-suspended' : ''}`}
                        onClick={() => openModal(7, item)}
                      >
                        {isCustomDiscount ? 'Custom discount' : userLevel.title}
                        {' '}
                        <Badge className={classNames({ 'badge-purple': isCustomDiscount }, { 'badge-gray': !isCustomDiscount })}>
                          {item.discount && item.discount > 0 ? item.discount : userLevel.discount}
                          %
                        </Badge>
                      </Button>
                    ) : (
                      <Button
                        bsSize="xsmall"
                        className={`${item.discount === 0 ? 'users-table__rates-btn' : ''} ${item.is_inactive ? 'users-table__btn-suspended' : ''}`}
                        onClick={() => openModal(7, item)}
                      >
                        {item.discount > 0 ? (
                          <>
                            <span>Discount</span>
                            {' '}
                            <Badge className="badge-purple">
                              {item.discount}
                              %
                            </Badge>
                          </>
                        ) : (
                          'Set discount'
                        )}
                      </Button>
                    )}
                  </td>
                );
              }

              case 'checkbox': {
                return <TableBodyCheckbox item={item} key={idx} values={values} onChangeCheck={onChangeCheck} />;
              }

              case 'email': {
                return (
                  <td key={idx}>
                    <span>{item.email}</span>
                    {' '}
                    {item.un_confirmed_title && (
                      <Tooltip placement="top" tooltipClass="tooltip__top tooltip__wide" text={item.un_confirmed_title}>
                        <span className="far fa-times-square" />
                      </Tooltip>
                    )}
                    {item.is_confirmed_title && !item['2fa'] && (
                      <Tooltip placement="top" tooltipClass="tooltip__top tooltip__wide" text={item.is_confirmed_title}>
                        <span className="far fa-check-square" />
                      </Tooltip>
                    )}
                    {item.is_signed_with_google && !item['2fa'] && (
                      <Tooltip placement="top" tooltipClass="tooltip__top tooltip__wide" text={item.is_signed_with_google}>
                        <span className="fab fa-google" />
                      </Tooltip>
                    )}
                    {item['2fa'] ? (
                      <Tooltip placement="top" tooltipClass="tooltip__top tooltip__wide" text={item['2fa_title']}>
                        <span className="far fa-shield-check" />
                      </Tooltip>
                    ) : null}
                  </td>
                );
              }

              case 'fields':
                return <BodyFields key={idx} details={elem.value} fields={fields} />;

              case 'id':
                return (
                  <td className="p-l" key={idx}>
                    {elem.value}
                  </td>
                );

              default: {
                return <td key={idx}>{elem.value}</td>;
              }
              }
            })}
        </tr>
      ))}
    </tbody>
  );
};

TableBody.propTypes = {
  isLoading: PropTypes.bool,
  isLoadingItems: PropTypes.array,
  users: PropTypes.array,
  handleSelect: PropTypes.func,
  onChangeCheck: PropTypes.func,
  values: PropTypes.array,
  openModal: PropTypes.func,
  config: PropTypes.object,
  fields: PropTypes.array,
};

export default TableBody;
